import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { IProduct } from "../../../models/catalogue";
import { isoProductID } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { defaults } from "../defaults";
import { IModularConfiguratorOptionSet } from "../models";
import { setSelectedRootProduct } from "../reducers";
import { rootProductSelector } from "../selectors";
import { getDefaultRootProduct } from "../utils";

type IProps = Immutable<{
    optionSet: IModularConfiguratorOptionSet;
}>;

const buildOptions = (products: readonly IProduct[]): React.ReactNode => {
    return products.map((rootProduct) => (
        <option
            key={`${rootProduct.id}`}
            value={isoProductID.unwrap(rootProduct.id)}
        >
            {rootProduct.title}
        </option>
    ));
};

export const DropdownRootProductSelector = (props: IProps): React.ReactNode => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(
        (rootState) => rootState.configurator || defaults,
    );
    const rootProducts = state.entities.rootProducts;
    const selectedRootProduct = rootProductSelector(state);

    const onChange = (e: React.FormEvent<HTMLSelectElement>) => {
        e.preventDefault();
        const rootProductID = isoProductID.wrap(
            parseInt(e.currentTarget.value, 10),
        );
        const rootProduct = rootProducts.find((p) => p.id === rootProductID);
        if (rootProduct) {
            dispatch(setSelectedRootProduct(rootProduct));
        }
    };

    if (rootProducts.length <= 1) {
        return null;
    }

    const htmlID = "rootProduct";
    const value = selectedRootProduct?.id
        ? selectedRootProduct.id
        : getDefaultRootProduct(rootProducts).id;
    const options = buildOptions(rootProducts);
    const configuratorClass = classNames({
        "configurator__select-container": true,
        [`configurator__select-container--${props.optionSet.layout.design}`]: true,
    });
    const prefixClass = classNames({
        configurator__prefix: true,
        [`configurator__prefix--${props.optionSet.layout.design}`]: true,
    });
    const selectClass = classNames({
        configurator__select: true,
        [`configurator__select--${props.optionSet.layout.design}`]: true,
    });

    return (
        <div className="configurator__option-container">
            <label htmlFor={htmlID} className={prefixClass}>
                {props.optionSet.root_product_selector.label}
            </label>
            <div className={configuratorClass}>
                <span className="ada-screenreader-only">
                    {t`Activating these elements will cause content on the page to be updated.`}
                </span>
                <select
                    id={htmlID}
                    value={isoProductID.unwrap(value)}
                    onChange={onChange}
                    className={selectClass}
                >
                    {options}
                </select>
            </div>
        </div>
    );
};
